/**
 * Eligibility - who is entitled to a lunch, and why not.
 *
 * This module is deliberately the *only* place that answers "does this person
 * get a break today?". The solver never re-litigates it, so a manager who
 * disagrees with an outcome has exactly one screen of rules to look at.
 *
 * Two design decisions are worth calling out:
 *
 *  1. **Every record gets a result, including blanks and absences.** The old
 *     manual process silently dropped people; the commonest support question
 *     was "why is Jonathan not on the list?". `assessDay` therefore returns one
 *     entry per rota row so the UI can always answer that question.
 *  2. **`reasonText` is always a finished sentence containing the real
 *     numbers.** A reason code alone ("too-short") forces every consumer to
 *     re-derive the explanation and get the wording subtly different.
 *
 * Pure logic: no DOM, no storage, no mutation of anything handed in.
 */

import {
  DEFAULT_SETTINGS,
  LUNCH_GROUPS,
  OVERNIGHT_POLICIES,
  SIX_HOUR_POLICIES,
  resolveDayConfig,
} from '../core/settings.js';
import { preferredTimeMinutes, ruleAppliesToShift, ruleFor } from '../core/model.js';
import {
  DAY_LABELS,
  MINUTES_PER_DAY,
  formatDuration,
  formatMinutes,
  parseTimeToMinutes,
} from '../core/time.js';
import { classifyShiftGroup, windowForGroup } from './grouping.js';

/**
 * Reason codes. `NO_VALID_SLOT` is never produced here - eligibility only says
 * whether a lunch is *owed*; the solver decides whether one can actually be
 * placed and reuses the code so the UI has a single vocabulary.
 */
export const INELIGIBLE = Object.freeze({
  BLANK: 'blank',
  ABSENT: 'absent',
  UNPARSED: 'unparsed',
  TOO_SHORT: 'too-short',
  SIX_HOUR_POLICY: 'six-hour-policy',
  OVERNIGHT: 'overnight',
  EXCLUDED_BY_RULE: 'excluded-by-rule',
  NO_VALID_SLOT: 'no-valid-slot',
});

/** Manager-facing wording for each policy value, used inside `reasonText`. */
const SIX_HOUR_POLICY_LABELS = {
  [SIX_HOUR_POLICIES.NO_LUNCH]: 'no lunch',
  [SIX_HOUR_POLICIES.LUNCH]: 'give a lunch',
  [SIX_HOUR_POLICIES.FLAG]: 'give a lunch and flag it for review',
};

const OVERNIGHT_POLICY_LABELS = {
  [OVERNIGHT_POLICIES.NONE]: 'no scheduled break',
  [OVERNIGHT_POLICIES.MID_SHIFT]: 'a break near the middle of the shift',
  [OVERNIGHT_POLICIES.NIGHT_WINDOW]: 'a break inside the night window',
};

/**
 * Confirmed operational lunch ladder for the common shift starts. Values are
 * the first-choice clock time; coverage, the compact run and NISCC may move a
 * person within the nearby range Josh supplied.
 */
const SHIFT_START_PREFERENCES = Object.freeze({
  [7 * 60]: preference(12 * 60),
  [7 * 60 + 30]: preference(12 * 60 + 30),
  [8 * 60]: preference(12 * 60 + 30, 13 * 60),
  [8 * 60 + 30]: preference(13 * 60, 13 * 60 + 30),
  [9 * 60]: preference(13 * 60, 14 * 60, 13 * 60 + 30),
  [10 * 60]: preference(14 * 60, 14 * 60 + 30),
  [11 * 60]: preference(15 * 60),
  // A midday start is a full eight-hour day: 16:00 puts the break four hours in
  // with four to go. 15:30 stays acceptable when coverage needs it, but eating
  // any earlier than that is barely into the shift.
  [12 * 60]: preference(15 * 60 + 30, 16 * 60, 16 * 60),
  [13 * 60]: preference(16 * 60),
  [14 * 60]: preference(18 * 60, 18 * 60 + 30),
  [15 * 60]: preference(18 * 60 + 30, 19 * 60),
  [16 * 60]: preference(19 * 60, 19 * 60 + 30),
  [17 * 60]: preference(19 * 60 + 30, 20 * 60),
});

/**
 * Assess one shift record.
 *
 * The order of the checks below is the business rule, not an implementation
 * detail - first match wins. In particular `forceLunchMinimumMinutes` (Alicia's
 * agreed exception) sits *after* exclusion, absence and the overnight policy but
 * *before* the length tests, because it is a promise about short shifts and not
 * a licence to schedule a break for somebody who is off, excluded or on nights.
 *
 * @param {object} shift A ShiftRecord from `core/model.js` - any `kind`.
 * @param {{ settings: object, dayConfig: object, rulesIndex: Map }} deps
 *   `dayConfig` may be omitted; it is then resolved from `settings` and the
 *   record's own day so a caller with only settings still gets real guard rails.
 * @returns {object} EligibilityResult (see docs/ARCHITECTURE.md §3.2).
 */
export function assessEligibility(shift, deps = {}) {
  const settings = deps.settings ?? DEFAULT_SETTINGS;
  const dayConfig = deps.dayConfig ?? resolveDayConfig(settings, shift?.dayKey);
  const rule = ruleFor(deps.rulesIndex, shift?.employeeId);

  const { group, source: groupSource } = classifyShiftGroup(shift, settings, rule);
  const base = {
    employeeId: shift?.employeeId ?? '',
    name: shift?.name ?? '',
    dayKey: shift?.dayKey ?? '',
    shift: shift ?? null,
    rule,
    eligible: false,
    reason: null,
    reasonText: '',
    forced: false,
    flagged: false,
    group,
    groupSource,
    window: windowForGroup(group, dayConfig),
    // Only a parsed shift can have a placement; nulls keep the UI honest.
    preferredMinutes: null,
    preferredRangeStartMinutes: null,
    preferredRangeEndMinutes: null,
    earliestMinutes: null,
    latestMinutes: null,
  };

  /* 1. Nothing to schedule around. --------------------------------------- */
  if (shift?.kind !== 'shift') {
    const reason = nonShiftReason(shift?.kind);
    return { ...base, reason, reasonText: nonShiftText(shift, reason) };
  }

  // Placement is computed for ineligible entries too: the explain panel wants to
  // be able to say "she would have eaten at 13:00 had the shift been longer".
  const window = effectiveWindow(shift, group, settings, dayConfig);
  const placement = computePlacement(shift, group, window, settings, dayConfig, rule);
  const entry = { ...base, ...placement };
  const lunchMinutes = positive(settings.lunchDurationMinutes, DEFAULT_SETTINGS.lunchDurationMinutes);

  /* 2. Explicit exclusion beats everything else. ------------------------- */
  if (rule.excludeFromLunch === true) {
    return {
      ...entry,
      reason: INELIGIBLE.EXCLUDED_BY_RULE,
      reasonText: `Not eligible: a staff rule excludes ${who(shift)} from lunch scheduling, so the `
        + `${shiftSummary(shift)} on ${dayLabel(shift)} is skipped.`,
    };
  }

  /* 3. Overnight staff arrange their own breaks (by default). ------------ */
  const overnightPolicy = settings.overnightPolicy ?? DEFAULT_SETTINGS.overnightPolicy;
  if (shift.isOvernight && overnightPolicy === OVERNIGHT_POLICIES.NONE) {
    return {
      ...entry,
      reason: INELIGIBLE.OVERNIGHT,
      reasonText: `Not eligible: ${shiftPhrase(shift)} and finishes after midnight, and the `
        + `overnight policy is set to ${policyLabel(OVERNIGHT_POLICY_LABELS, overnightPolicy)}.`,
    };
  }

  /* 4. The agreed per-employee exception. -------------------------------- */
  const forceMinimum = rule.forceLunchMinimumMinutes;
  if (Number.isFinite(forceMinimum) && shift.durationMinutes >= forceMinimum) {
    return grant(entry, {
      forced: true,
      lead: `Eligible: a staff rule grants ${who(shift)} a lunch on any shift of `
        + `${formatDuration(forceMinimum)} or longer, and ${shiftPhrase(shift)}.`,
    });
  }

  /* 5. Too short to break up. -------------------------------------------- */
  const minimumForLunch = number(settings.minShiftMinutesForLunch, DEFAULT_SETTINGS.minShiftMinutesForLunch);
  if (shift.durationMinutes < minimumForLunch) {
    return {
      ...entry,
      reason: INELIGIBLE.TOO_SHORT,
      reasonText: `Not eligible: ${shiftPhrase(shift)}, under the `
        + `${formatDuration(minimumForLunch)} minimum.`,
    };
  }

  /* 6. A 17:00-23:00 late pattern joins the later lunch run. ------------- */
  if (isConfirmedLateShortShift(shift, group)) {
    return grant(entry, {
      lead: `Eligible: ${shiftPhrase(shift)}, and this confirmed late-shift pattern joins the `
        + 'evening lunch run.',
    });
  }

  /* 7. The undocumented "about six hours" band. -------------------------- */
  const bandMin = number(settings.ambiguousShiftMinMinutes, DEFAULT_SETTINGS.ambiguousShiftMinMinutes);
  const bandMax = number(settings.ambiguousShiftMaxMinutes, DEFAULT_SETTINGS.ambiguousShiftMaxMinutes);
  if (shift.durationMinutes >= bandMin && shift.durationMinutes <= bandMax) {
    const policy = settings.sixHourPolicy ?? DEFAULT_SETTINGS.sixHourPolicy;
    const bandPhrase = `${shiftPhrase(shift)}, inside the ${formatDuration(bandMin)} to `
      + `${formatDuration(bandMax)} band where the six-hour policy says `
      + `${policyLabel(SIX_HOUR_POLICY_LABELS, policy)}`;

    if (policy === SIX_HOUR_POLICIES.LUNCH) {
      return grant(entry, { lead: `Eligible: ${bandPhrase}.` });
    }
    if (policy === SIX_HOUR_POLICIES.FLAG) {
      return grant(entry, { flagged: true, lead: `Eligible but flagged for review: ${bandPhrase}.` });
    }
    return {
      ...entry,
      reason: INELIGIBLE.SIX_HOUR_POLICY,
      reasonText: `Not eligible: ${bandPhrase}.`,
    };
  }

  /* 8. A normal, long enough shift. -------------------------------------- */
  if (shift.isOvernight) {
    const detail = overnightPolicy === OVERNIGHT_POLICIES.MID_SHIFT
      ? `places the break near the middle of the shift, between ${formatMinutes(window.start)} and `
        + `${formatMinutes(window.end)}`
      : `places the break inside the ${formatMinutes(window.start)}-${formatMinutes(window.end)} `
        + 'night window';
    return grant(entry, {
      lead: `Eligible: ${shiftPhrase(shift)} and finishes after midnight, so the overnight `
        + `policy ${detail}.`,
    });
  }

  return grant(entry, {
    lead: `Eligible: ${shiftPhrase(shift)}, at or over the ${formatDuration(minimumForLunch)} `
      + `minimum, so a lunch of ${formatDuration(lunchMinutes)} is due.`,
  });
}

/**
 * Assess a whole day.
 *
 * Returns one result per input record - blanks and absences included - in the
 * order given, because the day card lists everybody on the rota and needs a
 * ready-made explanation beside each name. The day config is resolved once and
 * shared, so a 30-row rota does not re-merge the settings layers 30 times.
 *
 * @param {object[]} shifts ShiftRecords for one day.
 * @param {{ settings: object, dayConfig: object, rulesIndex: Map }} deps
 * @returns {object[]} EligibilityResult[]
 */
export function assessDay(shifts, deps = {}) {
  if (!Array.isArray(shifts)) return [];
  const settings = deps.settings ?? DEFAULT_SETTINGS;
  const resolved = {
    settings,
    dayConfig: deps.dayConfig ?? resolveDayConfig(settings, shifts[0]?.dayKey),
    rulesIndex: deps.rulesIndex,
  };
  return shifts.map((shift) => assessEligibility(shift, resolved));
}

/* -------------------------------------------------------------------------- */
/* Windows and placement                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The window a shift actually searches in.
 *
 * Day and evening shifts use the group window straight from the day profile.
 * Genuine overnight shifts cannot: their clock times live past 1440, so a
 * 12:00–16:00 window would be hours behind them. Both overnight policies build
 * a window on the shift's own number line instead.
 */
function effectiveWindow(shift, group, settings, dayConfig) {
  if (group === LUNCH_GROUPS.NIGHT && shift.isOvernight) {
    const policy = settings?.overnightPolicy ?? DEFAULT_SETTINGS.overnightPolicy;
    return policy === OVERNIGHT_POLICIES.MID_SHIFT
      ? midShiftWindow(shift, settings)
      : nightWindow(shift, settings);
  }
  return windowForGroup(group, dayConfig);
}

/**
 * "Break near the middle of the shift": one slot step either side of the
 * midpoint. Wide enough that two night staff are not fighting over one slot,
 * tight enough that the break is still recognisably mid-shift.
 */
function midShiftWindow(shift, settings) {
  const interval = positive(settings?.slotIntervalMinutes, DEFAULT_SETTINGS.slotIntervalMinutes);
  const midpoint = Math.round((shift.startMinutes + shift.endMinutes) / 2);
  return { start: midpoint - interval, end: midpoint + interval };
}

/**
 * The configured night window, lifted onto the shift's number line.
 *
 * `nightWindowStart` is stored as a wall-clock string, so 01:00 parses to 60.
 * During a 22:00–08:00 shift (1320 → 1920) that 60 means *tomorrow* 01:00, i.e.
 * 1500. Anything below the shift start therefore gains a day, and a window that
 * wraps midnight relative to its own start gains another so it never inverts.
 */
function nightWindow(shift, settings) {
  const rawStart = parseTimeToMinutes(settings?.nightWindowStart ?? DEFAULT_SETTINGS.nightWindowStart) ?? 60;
  const rawEnd = parseTimeToMinutes(settings?.nightWindowEnd ?? DEFAULT_SETTINGS.nightWindowEnd) ?? 240;
  const start = rawStart < shift.startMinutes ? rawStart + MINUTES_PER_DAY : rawStart;
  let end = rawEnd < shift.startMinutes ? rawEnd + MINUTES_PER_DAY : rawEnd;
  if (end < start) end += MINUTES_PER_DAY;
  return { start, end };
}

/**
 * Work out the ideal lunch start and the hard bounds around it.
 *
 * Common shift starts use the confirmed operational ladder in docs/RULES.md §3;
 * an unusual start falls back to `start + 4h30` clamped into the group window.
 * For daytime shifts lasting ten hours or more, that target is additionally
 * clamped into the configured 13:30-14:30 long-shift range. An employee's own
 * `preferredLunchTime` replaces either default outright. Optional
 * `earliestLunchTime` / `latestLunchTime` staff-rule bounds are hard limits,
 * so compacting the rota or protecting coverage cannot pull a confirmed break
 * outside its agreed clock-time window.
 *
 * When the guard rails contradict each other (`earliest > latest`, e.g. a
 * 13:00–21:00 shift whose 120-minute settling-in period starts after the day
 * window has closed) the bounds are left crossed on purpose. Swapping or
 * widening them here would hide a real conflict; instead the entry stays
 * eligible, the solver fails it with `no-valid-slot`, and the manager sees a
 * warning they can override.
 */
/**
 * The fixed lunch time for a shift pattern, or null.
 *
 * Matched on the exact start and end of the shift, because that is how the rule
 * is stated operationally ("a one-to-nine eats at four") — a 13:00-19:00 shift
 * is a different pattern and gets the ordinary ladder treatment.
 */
function mandatoryLunchFor(shift, settings) {
  const patterns = Array.isArray(settings?.mandatoryShiftLunches)
    ? settings.mandatoryShiftLunches
    : [];
  for (const pattern of patterns) {
    const start = parseTimeToMinutes(pattern?.startTime);
    const end = parseTimeToMinutes(pattern?.endTime);
    const lunch = parseTimeToMinutes(pattern?.lunchTime);
    if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(lunch)) continue;
    // The rota stores an end past midnight as >1440, so compare on the same line.
    const shiftEnd = shift.endMinutes > MINUTES_PER_DAY && end < MINUTES_PER_DAY
      ? end + MINUTES_PER_DAY
      : end;
    if (shift.startMinutes === start && shift.endMinutes === shiftEnd) {
      return { minutes: lunch, label: pattern.label ?? `${pattern.startTime}-${pattern.endTime}` };
    }
  }
  return null;
}

function computePlacement(shift, group, window, settings, dayConfig, rule) {
  const interval = positive(settings?.slotIntervalMinutes, DEFAULT_SETTINGS.slotIntervalMinutes);
  const lunchMinutes = positive(settings?.lunchDurationMinutes, DEFAULT_SETTINGS.lunchDurationMinutes);

  // A per-employee guard rail replaces the day profile's, it does not add to it.
  const afterStart = Number.isFinite(rule?.minMinutesAfterShiftStart)
    ? rule.minMinutesAfterShiftStart
    : number(dayConfig?.minMinutesAfterShiftStart, 0);
  const beforeEnd = Number.isFinite(rule?.minMinutesBeforeShiftEnd)
    ? rule.minMinutesBeforeShiftEnd
    : number(dayConfig?.minMinutesBeforeShiftEnd, 0);

  /*
   * Clock guidance on a staff rule only governs the shifts it was agreed for.
   *
   * These rules are written about a shift - "Rachel works 09:00-21:00 on Monday
   * and should take lunch around 14:30" - but were being applied to every shift
   * she works. On her 07:00-15:00 Saturday that 14:30 clamps to 13:30, leaving
   * an hour of the shift after it, and pushes her behind people who started an
   * hour later. A rule with no `appliesToShifts` is still blanket, which is what
   * "whenever she is working" rules need.
   */
  const clockRuleApplies = ruleAppliesToShift(rule, shift);
  const ruleEarliest = clockRuleApplies ? staffClockMinutes(rule?.earliestLunchTime, shift) : null;
  const ruleLatest = clockRuleApplies ? staffClockMinutes(rule?.latestLunchTime, shift) : null;
  const hardEarliestMinutes = Math.max(
    shift.startMinutes + afterStart,
    Number.isFinite(ruleEarliest) ? ruleEarliest : -Infinity,
  );
  const hardLatestMinutes = Math.min(
    shift.endMinutes - lunchMinutes - beforeEnd,
    Number.isFinite(ruleLatest) ? ruleLatest : Infinity,
  );
  const earliestMinutes = Math.max(window.start, hardEarliestMinutes);
  const latestMinutes = Math.min(window.end, hardLatestMinutes);

  const rulePreference = clockRuleApplies ? preferredTimeMinutes(rule, shift.dayKey) : null;
  const desired = Number.isFinite(rulePreference)
    ? preference(rulePreference)
    : defaultPlacementPreference(shift, group, settings, dayConfig);
  const target = desired.target;

  const inWindow = clamp(target, window.start, window.end);
  const preferredMinutes = earliestMinutes > latestMinutes
    ? window.start
    : clamp(
      Math.round(clamp(inWindow, earliestMinutes, latestMinutes) / interval) * interval,
      earliestMinutes,
      latestMinutes,
    );
  const preferredRangeStartMinutes = earliestMinutes > latestMinutes
    ? preferredMinutes
    : snapPreferredRange(desired.start, interval, earliestMinutes, latestMinutes);
  const preferredRangeEndMinutes = earliestMinutes > latestMinutes
    ? preferredMinutes
    : snapPreferredRange(desired.end, interval, earliestMinutes, latestMinutes);

  // A fixed shift lunch replaces the ladder outright and widens the guard rails
  // just enough to reach it, so the time cannot be ruled out by the very bounds
  // that exist to keep an ordinary lunch sensible.
  const mandatory = mandatoryLunchFor(shift, settings);
  if (mandatory && mandatory.minutes + lunchMinutes <= shift.endMinutes
    && mandatory.minutes >= shift.startMinutes) {
    return {
      window,
      hardEarliestMinutes: Math.min(hardEarliestMinutes, mandatory.minutes),
      hardLatestMinutes: Math.max(hardLatestMinutes, mandatory.minutes),
      earliestMinutes: Math.min(earliestMinutes, mandatory.minutes),
      latestMinutes: Math.max(latestMinutes, mandatory.minutes),
      preferredMinutes: mandatory.minutes,
      preferredRangeStartMinutes: mandatory.minutes,
      preferredRangeEndMinutes: mandatory.minutes,
      mandatoryMinutes: mandatory.minutes,
      mandatoryLabel: mandatory.label,
    };
  }

  return {
    window,
    hardEarliestMinutes,
    hardLatestMinutes,
    earliestMinutes,
    latestMinutes,
    preferredMinutes,
    preferredRangeStartMinutes: Math.min(preferredRangeStartMinutes, preferredRangeEndMinutes),
    preferredRangeEndMinutes: Math.max(preferredRangeStartMinutes, preferredRangeEndMinutes),
    mandatoryMinutes: null,
    mandatoryLabel: null,
  };
}

/**
 * Keep ordinary shifts on the historical start + 4h30 rule, but move a
 * ten-hour-or-longer daytime shift into its later preferred range. This range
 * is deliberately a preference rather than a hard guard rail: coverage may
 * move the lunch when it genuinely has to, while staff-specific clock rules
 * (Rachel, Rebekah, or any future rule) continue to take precedence.
 */
function defaultPlacementPreference(shift, group, settings, dayConfig) {
  const baseline = shift.startMinutes + number(dayConfig?.preferredOffsetMinutes, 0);
  const threshold = positive(
    settings?.longDayShiftThresholdMinutes,
    DEFAULT_SETTINGS.longDayShiftThresholdMinutes,
  );
  if (group !== LUNCH_GROUPS.DAY || shift.durationMinutes < threshold) {
    return SHIFT_START_PREFERENCES[shift.startMinutes]
      ?? ladderBetweenRungs(shift.startMinutes)
      ?? preference(baseline);
  }

  const preferredStart = parseTimeToMinutes(settings?.longDayLunchWindowStart)
    ?? parseTimeToMinutes(DEFAULT_SETTINGS.longDayLunchWindowStart);
  const preferredEnd = parseTimeToMinutes(settings?.longDayLunchWindowEnd)
    ?? parseTimeToMinutes(DEFAULT_SETTINGS.longDayLunchWindowEnd);
  if (!Number.isFinite(preferredStart)
    || !Number.isFinite(preferredEnd)
    || preferredStart > preferredEnd) {
    return preference(baseline);
  }

  return preference(preferredStart, preferredEnd, clamp(baseline, preferredStart, preferredEnd));
}

/** Build a preference range; omitted target means the first value. */
function preference(start, end = start, target = start) {
  return { start, end, target };
}

/** Ladder start times, ascending, so an off-rung start can be placed between two. */
const LADDER_STARTS = Object.keys(SHIFT_START_PREFERENCES)
  .map(Number)
  .filter(Number.isFinite)
  .sort((a, b) => a - b);

/**
 * Read the ladder for a start time that sits *between* two of its rungs.
 *
 * The ladder only names whole and half hours, and the fallback for anything else
 * was `start + preferredOffsetMinutes` - 4h30. But the ladder itself runs at
 * roughly start + 4h (14:00 eats at 18:00, 15:00 at 18:30, 16:00 at 19:00), so
 * the fallback overshot past the next rung: a 14:45 start was sent to 19:30
 * while a 16:00 start went to 19:00. Somebody who came in an hour and a quarter
 * earlier ate half an hour later, which is precisely backwards.
 *
 * Interpolating between the neighbouring rungs keeps the ladder monotonic: a
 * later start can never produce an earlier lunch than an earlier one.
 *
 * Returns null outside the ladder's range, where the caller's offset rule is
 * still the best available answer.
 */
function ladderBetweenRungs(startMinutes) {
  if (!Number.isFinite(startMinutes)) return null;
  if (startMinutes < LADDER_STARTS[0]) return null;
  if (startMinutes > LADDER_STARTS[LADDER_STARTS.length - 1]) return null;

  let lower = LADDER_STARTS[0];
  let upper = LADDER_STARTS[LADDER_STARTS.length - 1];
  for (const rung of LADDER_STARTS) {
    if (rung <= startMinutes) lower = rung;
    if (rung >= startMinutes) { upper = rung; break; }
  }
  if (lower === upper) return SHIFT_START_PREFERENCES[lower] ?? null;

  const below = SHIFT_START_PREFERENCES[lower];
  const above = SHIFT_START_PREFERENCES[upper];
  if (!below || !above) return null;

  const ratio = (startMinutes - lower) / (upper - lower);
  const between = (from, to) => Math.round(from + (to - from) * ratio);

  return preference(
    between(below.start, above.start),
    between(below.end, above.end),
    between(below.target, above.target),
  );
}

/** Clamp and snap one end of a preferred range onto the usable slot grid. */
function snapPreferredRange(value, interval, earliest, latest) {
  return clamp(Math.round(clamp(value, earliest, latest) / interval) * interval, earliest, latest);
}

/** The explicitly mentioned 17:00-23:00 six-hour pattern still receives lunch. */
function isConfirmedLateShortShift(shift, group) {
  return group === LUNCH_GROUPS.EVENING
    && shift.startMinutes === 17 * 60
    && shift.endMinutes === 23 * 60
    && shift.durationMinutes === 6 * 60;
}

/** Resolve a staff clock time onto the same day axis as an overnight shift. */
function staffClockMinutes(value, shift) {
  const minutes = parseTimeToMinutes(value);
  if (!Number.isFinite(minutes)) return null;
  return shift.crossesMidnight && minutes < shift.startMinutes
    ? minutes + MINUTES_PER_DAY
    : minutes;
}

/* -------------------------------------------------------------------------- */
/* Result and sentence builders                                               */
/* -------------------------------------------------------------------------- */

/** Finish an eligible result, appending the placement sentence to the lead. */
function grant(entry, { lead, forced = false, flagged = false }) {
  return {
    ...entry,
    eligible: true,
    reason: null,
    forced,
    flagged,
    reasonText: `${lead} ${placementText(entry)}`,
  };
}

/** The second half of every eligible sentence: where the lunch can go. */
function placementText(entry) {
  const {
    earliestMinutes,
    latestMinutes,
    preferredMinutes,
    preferredRangeStartMinutes,
    preferredRangeEndMinutes,
  } = entry;
  if (earliestMinutes > latestMinutes) {
    return `No slot fits, though: the earliest allowed start (${formatMinutes(earliestMinutes)}) is `
      + `after the latest (${formatMinutes(latestMinutes)}), so this one needs a manual override.`;
  }
  const preferredText = preferredRangeStartMinutes !== preferredRangeEndMinutes
    ? `inside the preferred ${formatMinutes(preferredRangeStartMinutes)}-${formatMinutes(preferredRangeEndMinutes)} range`
    : `at ${formatMinutes(preferredMinutes)}`;
  return `The break can start between ${formatMinutes(earliestMinutes)} and `
    + `${formatMinutes(latestMinutes)}, ideally ${preferredText}.`;
}

/** "the 09:00-14:00 shift is 5 hrs" - the clause every length sentence shares. */
function shiftPhrase(shift) {
  return `the ${shiftRange(shift)} shift is ${formatDuration(shift.durationMinutes)}`;
}

/** "06:30-16:00 shift (9 hrs 30 mins)" - the noun-phrase form. */
function shiftSummary(shift) {
  return `${shiftRange(shift)} shift (${formatDuration(shift.durationMinutes)})`;
}

/** "09:00-14:00". Hyphenated to match how managers write shifts on the rota. */
function shiftRange(shift) {
  return `${formatMinutes(shift.startMinutes)}-${formatMinutes(shift.endMinutes)}`;
}

/** Map a non-'shift' record kind onto its reason code. */
function nonShiftReason(kind) {
  if (kind === 'absent') return INELIGIBLE.ABSENT;
  if (!kind || kind === 'blank') return INELIGIBLE.BLANK;
  return INELIGIBLE.UNPARSED;
}

/** Explain a record that carries no times, naming the day and the raw cell. */
function nonShiftText(shift, reason) {
  const name = who(shift);
  const day = dayLabel(shift);
  const raw = String(shift?.raw ?? '').trim();

  if (reason === INELIGIBLE.ABSENT) {
    return `Not eligible: ${name} is marked absent on ${day}${raw ? ` ("${raw}")` : ''}.`;
  }
  if (reason === INELIGIBLE.UNPARSED) {
    return `Not eligible: the ${day} cell for ${name} reads "${raw}", which could not be read as a `
      + 'start and end time - fix the rota entry and regenerate.';
  }
  return `Not eligible: ${name} is not rostered on ${day}, so there is no shift to break up.`;
}

/** Prefer the rota name; fall back to something that still reads as a sentence. */
function who(shift) {
  const name = String(shift?.name ?? '').trim();
  return name || 'this employee';
}

/** "Monday", or a neutral phrase when the record has no recognisable day. */
function dayLabel(shift) {
  return DAY_LABELS[shift?.dayKey] ?? 'this day';
}

/** Policy wording, degrading to the raw value so an unknown policy is visible. */
function policyLabel(labels, value) {
  return labels[value] ?? String(value);
}

function clamp(value, low, high) {
  return Math.min(Math.max(value, low), high);
}

function number(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function positive(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}
